"use client"

import { useForm, usePage } from "@inertiajs/react"
import { AlertCircle, Mail01, Phone } from "@untitledui/icons"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { InputBase, TextField } from "@/components/ui/input"
import { Label } from "@/components/ui/input"
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from "@/components/ui/modal"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"
import SettingsLayout from "@/layouts/settings/layout"
import { toFieldError } from "@/lib/utils"
import { settingsProfilePath, usersPath } from "@/routes"

export default function Profile() {
  const { auth } = usePage().props
  const { data, setData, patch, processing, errors } = useForm({
    name: auth.user.name,
    mobile_phone: auth.user.mobile_phone ?? "",
  })

  const isOauthUser = auth?.user?.oauth_user

  const deleteAccountForm = useForm({
    password: "",
    delete_confirmation: "",
  })

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(settingsProfilePath())
  }

  const handleDeleteAccount = (e: React.FormEvent) => {
    e.preventDefault()
    deleteAccountForm.delete(usersPath(), {
      onSuccess: () => setIsDeleteModalOpen(false),
    })
  }

  return (
    <SettingsLayout>
      <form className="flex flex-col gap-6 px-4 lg:px-8" onSubmit={submit}>
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>Personal info</SectionHeader.Heading>
              <SectionHeader.Subheading>
                Update your personal details here.
              </SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="Name"
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="name"
              value={data.name}
              onChange={(v) => setData("name", v)}
              isInvalid={!!errors.name}
              error={toFieldError(errors.name)}
            >
              <Label className="lg:hidden">Name</Label>
              <InputBase size="md" />
            </TextField>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Mobile number"
              description="The phone that rings first when you call a school from its card."
              className="max-lg:hidden"
            />

            <TextField
              name="mobile_phone"
              type="tel"
              value={data.mobile_phone}
              onChange={(v) => setData("mobile_phone", v)}
              isInvalid={!!errors.mobile_phone}
              error={toFieldError(errors.mobile_phone)}
            >
              <Label className="lg:hidden">Mobile number</Label>
              <InputBase size="md" icon={Phone} placeholder="04xx xxx xxx" />
            </TextField>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Email address"
              className="max-lg:hidden"
            />

            <TextField
              isDisabled
              name="email"
              type="email"
              value={auth.user.email}
            >
              <Label className="lg:hidden">Email address</Label>
              <InputBase size="md" icon={Mail01} />
            </TextField>
          </div>
        </div>

        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing}
            >
              Save
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </form>

      <div className="mt-8 px-4 lg:px-8">
        <hr className="bg-border-secondary mb-8 h-px w-full border-none" />

        <div className="flex flex-col gap-4">
          <h3 className="text-error-primary text-lg font-semibold">
            Danger Zone
          </h3>

          <div className="caution-band flex flex-col items-start justify-between gap-4 p-4 md:flex-row md:items-center">
            <div>
              <h4 className="text-error-primary text-sm font-semibold">
                Delete account
              </h4>
              <p className="text-error-primary text-sm">
                Permanently delete your account and all of your content.
              </p>
            </div>

            <DialogTrigger
              isOpen={isDeleteModalOpen}
              onOpenChange={setIsDeleteModalOpen}
            >
              <Button color="primary-destructive" size="md">
                Delete account
              </Button>
              <ModalOverlay isDismissable>
                <Modal>
                  <Dialog className="mx-auto mt-20 flex w-full max-w-md flex-col gap-6 rounded-xl bg-white p-6 shadow-xl md:mt-0">
                    <div className="flex gap-4">
                      <div className="bg-caution-50 text-caution-700 ring-caution-500 h-fit shrink-0 rounded-full p-3 ring-1 ring-inset">
                        <AlertCircle className="size-6" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <h3 className="text-primary text-lg font-semibold">
                          Delete account
                        </h3>
                        <p className="text-tertiary text-sm">
                          Are you sure you want to delete your account? This
                          action cannot be undone.
                        </p>
                      </div>
                    </div>

                    <form
                      onSubmit={handleDeleteAccount}
                      className="flex flex-col gap-4"
                    >
                      {isOauthUser ? (
                        <TextField
                          isRequired
                          name="delete_confirmation"
                          value={deleteAccountForm.data.delete_confirmation}
                          onChange={(v) =>
                            deleteAccountForm.setData("delete_confirmation", v)
                          }
                          isInvalid={
                            !!deleteAccountForm.errors.delete_confirmation
                          }
                          error={toFieldError(
                            deleteAccountForm.errors.delete_confirmation,
                          )}
                        >
                          <Label>Type DELETE to confirm</Label>
                          <InputBase placeholder="DELETE" size="md" />
                        </TextField>
                      ) : (
                        <TextField
                          isRequired
                          name="password"
                          value={deleteAccountForm.data.password}
                          onChange={(v) =>
                            deleteAccountForm.setData("password", v)
                          }
                          isInvalid={!!deleteAccountForm.errors.password}
                          error={toFieldError(
                            deleteAccountForm.errors.password,
                          )}
                        >
                          <Label>Confirm password</Label>
                          <InputBase
                            type="password"
                            placeholder="Enter your password"
                            size="md"
                          />
                        </TextField>
                      )}

                      <div className="mt-2 flex justify-end gap-3">
                        <Button
                          color="secondary"
                          onPress={() => setIsDeleteModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          color="primary-destructive"
                          isDisabled={deleteAccountForm.processing}
                        >
                          Delete account
                        </Button>
                      </div>
                    </form>
                  </Dialog>
                </Modal>
              </ModalOverlay>
            </DialogTrigger>
          </div>
        </div>
      </div>
    </SettingsLayout>
  )
}
